<template>
	<Dialog
		:dialog-visible="modelValue"
		:title="''"
		:options="{ width: '760px', fullHeight: true }"
		message=""
		agree-text="Reschedule Call"
		cancel-text="Cancel"
		:agree-loading="saving"
		@agree="submit"
		@cancel="close"
		@update:dialog-visible="close">
		<template #dialog-header>
			<div class="reschedule-header">
				<h5 v-test="'reschedule-dialog'" class="reschedule-title">
					<span class="reschedule-title-accent">Reschedule</span> a Live Video Call.
				</h5>
				<div class="reschedule-timezone">{{ timeZoneLine }}</div>
			</div>
		</template>

		<template #dialog-body>
			<div class="reschedule-current">
				<div class="reschedule-current-label">Currently scheduled</div>
				<div class="reschedule-current-time">
					<v-icon class="reschedule-current-icon">icon-calendar-check-stroke</v-icon>
					<span>{{ currentWindowLabel }}</span>
				</div>
			</div>

			<v-row class="reschedule-pickers">
				<v-col cols="12" md="7" class="py-1">
					<div class="reschedule-label">New date</div>
					<!-- LSP-16658: `hide-header` is load-bearing, not cosmetic. v-date-picker's default
					     header ("SELECT DATE" plus a large "Sat, Sep 19" line) is ~140px of chrome, and with it
					     the body needed 799px in the 658px this dialog gets — the message box and both actions
					     sat below the fold. ActionSchedule keeps its header because the calendar owns a whole
					     step there; here the calendar, the summary and the message share one screen.
					     `control-variant="modal"` stays, matching ActionSchedule: it keeps month and year in a
					     single row, where the default variant splits them into two separate steppers. -->
					<div class="reschedule-picker-panel">
						<v-date-picker
							v-model="selectedDateModel"
							v-test="'reschedule-date'"
							width="100%"
							control-variant="modal"
							color="primary"
							hide-header
							:min="minimumDate" />
					</div>
				</v-col>
				<v-col cols="12" md="5" class="py-1">
					<div class="reschedule-label">New time</div>
					<div class="reschedule-picker-panel">
						<v-list ref="timeList" v-test="'reschedule-time'" class="reschedule-time-list" density="compact">
							<v-list-item
								v-for="slot in timeSlots"
								:key="slot.value"
								:class="{ 'bg-primary': slot.value === selectedTime }"
								:active="slot.value === selectedTime"
								:disabled="slot.disabled"
								@click="selectedTime = slot.value">
								<v-list-item-title>{{ slot.text }}</v-list-item-title>
							</v-list-item>
						</v-list>
					</div>
				</v-col>
			</v-row>

			<div class="reschedule-summary">
				<span class="reschedule-summary-from">{{ currentSummaryLabel }}</span>
				<v-icon size="small">icon-arrow-left-right</v-icon>
				<span class="reschedule-summary-to">{{ newSummaryLabel }}</span>
				<div v-if="isNonexistentLocalTime" v-test="'reschedule-nonexistent-time-error'" class="reschedule-error">
					That time doesn't exist on this date because the clocks move forward. Pick another time.
				</div>
				<div v-else-if="isInThePast" v-test="'reschedule-past-time-error'" class="reschedule-error">Pick a time in the future.</div>
				<!-- LSP-16658 (Bugbot): the one invalid state that had NO explanation. A call whose time has
				     already passed opens on today with no slot chosen (see prefill), so the very first thing
				     the user sees is a submit that silently does nothing. Every other invalid state says why;
				     an always-enabled action has to reveal the invalid input rather than fail silently. -->
				<div v-else-if="!selectedTime" v-test="'reschedule-no-time-hint'" class="reschedule-hint">Pick a time to reschedule.</div>
				<div v-else-if="isUnchanged" v-test="'reschedule-unchanged-hint'" class="reschedule-hint">
					Pick a new date or time to reschedule.
				</div>
				<!-- Deliberately OUTSIDE the v-else-if chain above: an ambiguous time is perfectly valid, so
				     this must also show alongside an otherwise-submittable selection. It informs, it never
				     blocks. -->
				<div v-if="ambiguousLocalTimeAbbreviation" v-test="'reschedule-ambiguous-time-hint'" class="reschedule-hint">
					The clocks go back on this date, so this time happens twice. We'll use {{ ambiguousLocalTimeAbbreviation }}.
				</div>
			</div>

			<div class="reschedule-note-block">
				<div class="reschedule-note-label">
					<strong>{{ contactFirstName }} gets a text and email with the new time.</strong>&nbsp;<span class="reschedule-note-hint"
						>Edit the message below if you want.</span
					>
				</div>
				<!-- Only when the contact's zone differs from the one the pickers above are in. The times
				     inside the message are then a different wall clock from the ones in the summary, which
				     is correct and would otherwise read as a bug. -->
				<div v-if="messageZoneNote" v-test="'reschedule-message-zone-note'" class="reschedule-hint">
					Times in this message are in {{ messageZoneNote }}, the time zone this call was booked in.
				</div>
				<AppTextarea
					v-model="note"
					v-test="'reschedule-note'"
					rows="2"
					auto-grow
					:character-limit="NOTE_MAX_LENGTH"
					over-limit-message="Please enter a shorter message."
					placeholder="Add a message for your contact..."
					@keydown.enter.stop />
			</div>
		</template>
	</Dialog>
</template>

<script>
	import moment from "moment-timezone";
	import Dialog from "@/components/Dialog.vue";
	import { getDateTokens } from "@/helpers/dateFormat";
	import { applyScheduledCallMessageTokens, renderScheduledCallTokens } from "@/helpers/scheduledCallMessage";
	import { isAmbiguousWallClock, isNonexistentWallClock, resolveZoneLabelInstant } from "@/helpers/schedulingTimezone";
	import { AppTextarea } from "@common-ui/components";
	import { countCodePoints } from "@common-ui/utilities/characterCount";

	// Mirrors the server's RESCHEDULE_NOTE_MAX_CODE_POINTS
	// (apps/contact-api/src/controllers/conversations.ts). Counted in Unicode CODE POINTS on both
	// sides — AppTextarea's app-wide default unit is code points and the server measures
	// `utility.countCodePoints(note)` — so 500 astral-plane emoji are 500 here and 500 there.
	const NOTE_MAX_LENGTH = 500;

	// 15-minute booking slots, matching ActionSchedule.populateTimes.
	const SLOT_INTERVAL_MINUTES = 15;

	// How often the open dialog re-evaluates "now". The floor is minute-granular, so this only has to
	// be comfortably finer than a minute.
	const CLOCK_TICK_INTERVAL_MS = 15000;

	/**
	 * LSP-16658 — move an existing scheduled live call to a new date/time.
	 *
	 * The picker floor deliberately mirrors ActionSchedule.vue's create-time floor rather than
	 * imposing a minimum lead time: past calendar days are disabled in the SELECTED timezone, and on
	 * today the slots at or before the current minute are disabled. That is exactly the server's
	 * single gate (`startAt > now`, no lead time) — so nothing the picker offers can be rejected, and
	 * nothing the server would accept is hidden.
	 *
	 * This dialog does not call the API. The parent owns the request (and therefore the `saving`
	 * flag) so an entity switch or a double-click can be arbitrated in one place.
	 */
	export default {
		components: { Dialog, AppTextarea },
		props: {
			modelValue: { type: Boolean, default: false },
			appointment: { type: Object, default: null },
			contactFirstName: { type: String, default: "Your contact" },
			// The zone the call is DISPLAYED in on the card. It renders the message's date/time tokens
			// here, and is sent back as `timeZone` on submit so the server's own fill (used when this
			// client sends an empty message) resolves to the same zone the user was looking at. The
			// server prefers the stored `Appointment.timeZone` over it — this is the fallback, and the
			// zone actually used is persisted back onto the appointment.
			timeZone: { type: String, required: true },
			dateFormat: { type: String, default: null },
			/**
			 * The organization's scheduled-call template (`Organization.notificationTemplateScheduled`) —
			 * the SAME text `ActionSchedule.vue` seeds the booking message from. Product owner decision,
			 * 2026-09-17: a reschedule is a new invitation made by the same mechanism as the original, so
			 * the message starts as this template rendered for the newly picked time and the user edits it
			 * from there. An organization that has cleared its template seeds an empty box, which is a
			 * supported state — the server renders the message itself when the client sends none.
			 */
			messageTemplate: { type: String, default: "" },
			/**
			 * Substituted for `${teamMember}` / `${user}`. The conversation's ASSIGNED user, resolved by
			 * the caller with the same firstname → lastname → email tiering the server uses, so the
			 * message the user edits is the message that gets sent.
			 */
			teamMemberName: { type: String, default: "" },
			/** Substituted for `${organization}`. */
			organizationName: { type: String, default: "" },
			/**
			 * The CONTACT-FACING zone — the stored `Appointment.timeZone`, i.e. the zone the booking's
			 * own copy was written in. The message's date/time tokens render in THIS, not in `timeZone`.
			 *
			 * The two are different things and conflating them was a HIGH review finding. `timeZone` is
			 * the zone this admin is working in (`moment.tz.guess()`), which is the right zone for the
			 * pickers and the old→new summary — those are theirs. But the server writes the session name
			 * and the ICS event summary in the booking zone, and both are contact-visible, so a message
			 * rendered in the admin's zone told the contact two wall-clock times for the same call
			 * whenever the two differed. // Bugbot review (High) "Contact copy uses admin timezone"
			 *
			 * Empty or unknown falls back to `timeZone`, matching the server's own resolution order
			 * (stored zone → request zone) including its treatment of an invalid stored zone as ABSENT.
			 */
			messageTimeZone: { type: String, default: "" },
			// Owned by the parent, not this dialog: the parent performs the API call, so it is the only
			// side that knows whether a request is in flight. A second internal flag here would have no
			// exit path on failure — the parent keeps the dialog open to let the user retry, and an
			// internal flag latched true would refuse every retry forever.
			saving: { type: Boolean, default: false }
		},
		emits: ["update:modelValue", "rescheduled"],
		data() {
			return {
				NOTE_MAX_LENGTH,
				selectedDate: "",
				selectedTime: "",
				note: "",
				// The date/time values the message currently quotes, so a later time change can be applied
				// as a targeted replace of exactly those strings rather than as a full re-render — which is
				// what lets the user's own edits to the surrounding sentence survive. `null` means "nothing
				// has been rendered into the box yet", which is the only state that justifies overwriting
				// it wholesale from the template.
				previousMessageTokens: null,
				timeSlots: [],
				// `moment()` is not reactive, so any computed reading it is cached until one of its
				// REACTIVE deps changes. A dialog left open would otherwise freeze both the date picker's
				// minimum day and the past-time check at the moment it was opened: submit could no-op on a
				// time that had since become valid, or send one the server then rejected. Every
				// clock-dependent computed reads this counter so a tick invalidates them.
				clockTick: 0,
				// Timer handle, not UI state. Cleared by stopClock() on close AND on unmount.
				clockIntervalId: null
			};
		},
		computed: {
			// LSP-21581: the instant whose abbreviation the two zone labels below describe -- never bare
			// `moment.tz(zone)`, which is the zone TODAY and said "EDT" over a 2 November call while every
			// contact-facing copy said EST.
			//
			// The label must name the SAME instant this dialog submits. That is `selectedMoment` itself,
			// taken as-is: submit() sends its valueOf, isInThePast gates on it, and the ambiguous-time hint
			// names its abbreviation, so inside a fall-back overlap all four agree on one occurrence. The
			// shared helper is used only before a full selection exists (its date-only and booked-start
			// anchors); routing the full selection through it re-resolved the overlap against the live
			// clock and had the header say EST while the hint beside it said "We'll use EDT". ActionSchedule
			// differs on purpose: its schedule() resolves through resolveBookableInstant, so its chip does
			// too. `clockTick` keeps the now-fallback from freezing in a dialog left open.
			zoneLabelInstant() {
				void this.clockTick;
				if (this.selectedMoment?.isValid()) {
					return this.selectedMoment;
				}
				return resolveZoneLabelInstant(this.selectedDate, "", this.timeZone, this.currentStart ?? moment());
			},
			timeZoneLine() {
				const abbreviation = this.zoneLabelInstant.format("z");
				return `${this.timeZone.replaceAll("_", " ")} · ${abbreviation}`;
			},
			/**
			 * The zone the CONTACT's copy is written in, resolved the way the server resolves it.
			 *
			 * Validated rather than trusted: `Appointment.timeZone` has no column constraint and the
			 * create route has no body schema, and `moment.tz` renders an unknown zone as UTC SILENTLY —
			 * so an unvalidated bad value here would put a UTC time in the contact's message while the
			 * server (which does validate, and self-heals the row) used something else entirely.
			 */
			contactFacingTimeZone() {
				const stored = this.messageTimeZone;
				return typeof stored === "string" && stored.length > 0 && moment.tz.zone(stored) ? stored : this.timeZone;
			},
			/**
			 * Only when the contact reads a different wall clock than the admin is picking in. Two zones
			 * on one screen is confusing; two zones on one screen with nothing saying so is a bug report.
			 */
			messageZoneNote() {
				if (this.contactFacingTimeZone === this.timeZone) {
					return "";
				}
				const abbreviation = this.zoneLabelInstant.clone().tz(this.contactFacingTimeZone).format("z");
				return `${this.contactFacingTimeZone.replaceAll("_", " ")} · ${abbreviation}`;
			},
			minimumDate() {
				// Floor is "today in the SELECTED zone", never the device's today — a device hours ahead
				// of the call's zone has already rolled over while the call's zone is still on the valid
				// day (same reasoning as ActionSchedule.updateMinimumDate).
				void this.clockTick;
				return moment().tz(this.timeZone).startOf("day").format("YYYY-MM-DD");
			},
			currentStart() {
				return this.appointment?.startAt ? moment(this.appointment.startAt).tz(this.timeZone) : null;
			},
			currentEnd() {
				return this.appointment?.endAt ? moment(this.appointment.endAt).tz(this.timeZone) : null;
			},
			currentWindowLabel() {
				if (!this.currentStart || !this.currentEnd) {
					return "";
				}
				const tokens = getDateTokens(this.dateFormat);
				return `${this.currentStart.format(tokens.weekdayMonthDayNoYear)} · ${this.currentStart.format("h:mma")} – ${this.currentEnd.format("h:mma")}`;
			},
			currentSummaryLabel() {
				return this.formatSummaryLabel(this.currentStart);
			},
			selectedMoment() {
				if (!this.selectedDate || !this.selectedTime) {
					return null;
				}
				// v-date-picker emits a Date at BROWSER-local midnight; moment.tz(date, zone) would
				// reinterpret that instant in the target zone and can shift the calendar day backward.
				// Compose a tz-naive "YYYY-MM-DD h:mm A" string instead (same fix as ActionSchedule).
				return moment.tz(`${this.selectedDate} ${this.selectedTime}`, "YYYY-MM-DD h:mm A", this.timeZone);
			},
			newSummaryLabel() {
				// Duration is preserved by the SERVER; this end time is display only, so the user can see
				// the window they are moving to rather than just its start.
				const start = this.selectedMoment;
				if (!start) {
					return "";
				}
				const label = this.formatSummaryLabel(start);
				if (!this.currentStart || !this.currentEnd) {
					return label;
				}
				const end = start.clone().add(this.currentEnd.valueOf() - this.currentStart.valueOf(), "milliseconds");
				return `${label} – ${end.format("h:mma")}`;
			},
			isInThePast() {
				void this.clockTick;
				if (!this.selectedMoment) {
					return false;
				}
				// STRICTLY future, which is the server's only gate ("Pick a time in the future."). Not
				// `isBefore`: a selection exactly equal to now is rejected there, so accepting it here
				// would offer the user a submit that always 400s.
				return !this.selectedMoment.isAfter(moment());
			},
			isUnchanged() {
				// Minute granularity on both sides: `startAt` from the database may carry seconds that
				// `selectedMoment` (built from whole-minute date/time picker values) never has, which
				// would otherwise make a genuinely unchanged submit look "changed" — and the server
				// rejects an unchanged start with a 400.
				return !!this.currentStart && !!this.selectedMoment && this.selectedMoment.isSame(this.currentStart, "minute");
			},
			// A spring-forward day's skipped hour (e.g. 2:30 AM when the clocks jump from 2:00 to 3:00)
			// has no corresponding real instant. `moment.tz(<naive>, zone)` silently NORMALIZES a
			// nonexistent local time to a different real one instead of refusing it, so without this
			// check the user picks 2:30 AM and the call books at a time they never chose, with no
			// warning at all. Detected by reformatting the parsed instant's local wall-clock time and
			// comparing it back against what was requested — a mismatch means moment had to shift it.
			isNonexistentLocalTime() {
				// Delegated to the shared helper (LSP-17619). This was the only implementation of the rule
				// in the app, and ActionSchedule -- the screen that CREATES these calls -- had none, so the
				// same nonexistent wall clock was refused here and silently booked an hour away there.
				return isNonexistentWallClock(this.selectedDate, this.selectedTime, this.timeZone);
			},
			// On a fall-back day the same wall clock occurs TWICE, an hour apart, and `moment.tz` silently
			// keeps the FIRST without saying so — so "1:30 AM" could book an hour away from what the user
			// meant, with nothing on screen to reveal it. Detected by asking whether the instant an hour
			// later renders the SAME wall-clock string; if it does, the time is ambiguous. Returns the
			// abbreviation actually being used (e.g. "EDT") so the copy can name it, or "" when there is
			// no ambiguity. This does NOT block submit: the selection is a real, valid instant.
			ambiguousLocalTimeAbbreviation() {
				if (!this.selectedMoment || this.isNonexistentLocalTime) {
					return "";
				}
				// Was a local `add(1, "hour")` probe. Same rule as the shared resolver's, so it is now the
				// shared resolver's — and it was wrong for the one zone that shifts by thirty minutes
				// (Australia/Lord_Howe), which silently dropped the disambiguating abbreviation there.
				return isAmbiguousWallClock(this.selectedMoment) ? this.selectedMoment.format("z") : "";
			},
			isNoteTooLong() {
				return countCodePoints(this.note || "") > NOTE_MAX_LENGTH;
			},
			isValid() {
				// No explicit selectedMoment.isValid() clause here, deliberately. An INVALID Moment is
				// truthy, so that looks like a gap — but isInThePast already refuses it: its condition is
				// `!selectedMoment.isAfter(moment())`, and isAfter on an invalid moment is false, so the
				// negation is true. Adding the clause changed no outcome and no test could distinguish it,
				// which is why it is not here. What DOES need guarding is the copy an invalid slot
				// produces, and that is fixed in renderScheduledCallTokens rather than by blocking twice.
				return (
					!!this.selectedMoment && !this.isNonexistentLocalTime && !this.isInThePast && !this.isUnchanged && !this.isNoteTooLong
				);
			},
			selectedDateModel: {
				get() {
					return this.selectedDate ? moment(this.selectedDate, "YYYY-MM-DD").toDate() : null;
				},
				set(value) {
					if (!value) {
						return;
					}
					this.selectedDate =
						value instanceof Date
							? `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`
							: value;
					this.populateTimeSlots();
				}
			}
		},
		watch: {
			modelValue(isOpen) {
				if (isOpen) {
					this.prefill();
					this.scrollTimeList();
					this.startClock();
				} else {
					this.stopClock();
				}
			},
			// Re-prefill ONLY when this is a genuinely different booking. The parent binds
			// `entity.Appointment`, and `entity` is replaced wholesale by socket pushes and refetches, so
			// this watcher fires on every such update with an equivalent appointment. Re-running prefill()
			// there overwrote the note the user was typing and reset the slot they had chosen, with no
			// warning, and a later submit then acted on the reset values (AI review finding 2). A
			// different booking id is the only thing that justifies discarding their work; for the same
			// booking the "Currently scheduled" panel still re-renders from the computeds.
			appointment(next, previous) {
				if (!this.modelValue) {
					return;
				}
				const isSameBooking = !!next?.id && !!previous?.id && next.id === previous.id;
				if (isSameBooking) {
					return;
				}
				this.prefill();
				this.scrollTimeList();
			},
			// The message quotes the selected date and time, so it has to follow them. Watchers rather than
			// a call inside each setter: the date arrives through `selectedDateModel`'s setter and the time
			// through an inline `@click` on the slot list, and a re-render attached to only one of those
			// would leave the message describing a slot on a different day.
			selectedDate() {
				this.renderMessage();
				this.scrollTimeList();
			},
			selectedTime() {
				this.renderMessage();
			}
		},
		mounted() {
			if (this.modelValue) {
				this.prefill();
				this.scrollTimeList();
				this.startClock();
			}
		},
		beforeUnmount() {
			// Clear on teardown as well as on close: the dialog can be unmounted while open (the host page
			// navigates away), and a surviving interval would tick against a destroyed component forever.
			this.stopClock();
		},
		methods: {
			scrollTimeList() {
				this.$nextTick(() =>
					requestAnimationFrame(() => {
						if (!this.modelValue) return;
						const list = this.$refs.timeList?.$el;
						const items = list?.querySelectorAll(".v-list-item");
						if (!items?.length) return;
						const time = moment(this.selectedTime || moment().tz(this.timeZone).format("h:mm A"), "h:mm A");
						if (!time.isValid()) return;
						const slotIndex = Math.min(
							Math.ceil((time.hours() * 60 + time.minutes()) / SLOT_INTERVAL_MINUTES),
							items.length - 1
						);
						const item = items[slotIndex];
						list.scrollTop = item.offsetTop - items[0].offsetTop + item.offsetHeight / 2 - list.clientHeight / 2;
					})
				);
			},
			startClock() {
				if (this.clockIntervalId !== null) {
					return;
				}
				this.clockIntervalId = setInterval(() => {
					this.clockTick += 1;
					// Re-derive the slot disabled-state too: it is built imperatively, so it does not track
					// the tick on its own. Deliberately does NOT move the user's current selection — the
					// inline past-time error tells them instead of silently choosing a different time.
					this.populateTimeSlots();
				}, CLOCK_TICK_INTERVAL_MS);
			},
			stopClock() {
				if (this.clockIntervalId === null) {
					return;
				}
				clearInterval(this.clockIntervalId);
				this.clockIntervalId = null;
			},
			// Shared by currentSummaryLabel and newSummaryLabel: the "from → to" summary row must format
			// both sides identically, or the pair reads as unrelated rather than as one comparison.
			formatSummaryLabel(momentValue) {
				if (!momentValue) {
					return "";
				}
				const tokens = getDateTokens(this.dateFormat);
				return `${momentValue.format(tokens.weekdayMonthDayShortNoYear)} · ${momentValue.format("h:mma")}`;
			},
			prefill() {
				// Cleared rather than seeded with fixed wording: the message is rendered from the
				// organization's template by renderMessage() once a date AND time exist, and `null` tokens
				// are what tell it this is a first render rather than a re-render over the user's edits.
				this.note = "";
				this.previousMessageTokens = null;
				// A call whose time has already passed is still reschedulable (that is the point of the
				// feature), but prefilling its OWN past date would open the picker on a day the floor
				// disables and a slot the floor disables — an initial state the user can only get out of
				// by noticing. Open on today with no slot chosen instead; the "Pick a time in the future."
				// message still covers the moment a past slot becomes selected some other way.
				if (this.currentStart && this.currentStart.isAfter(moment())) {
					this.selectedDate = this.currentStart.format("YYYY-MM-DD");
					this.selectedTime = this.currentStart.format("h:mm A");
				} else {
					this.selectedDate = moment().tz(this.timeZone).format("YYYY-MM-DD");
					this.selectedTime = "";
				}
				this.populateTimeSlots();
				this.renderMessage();
			},
			/**
			 * Put the organization's scheduled-call template, rendered for the CURRENT selection, into the
			 * message box — or, once something is already there, move only the date/time strings it quotes.
			 *
			 * The two cases are deliberately different operations, not one re-render with a guard:
			 *
			 *  - FIRST render (`previousMessageTokens === null`): substitute every placeholder in the
			 *    template. There is nothing of the user's to lose.
			 *  - RE-render: run the same substitution over what is in the box NOW — which has no
			 *    placeholders left, so that pass is a no-op — and then replace the PREVIOUSLY rendered
			 *    date/time strings with the new ones. Words the user changed around them are untouched.
			 *    A full re-render here would silently discard every edit each time they tried another slot.
			 *
			 * Nothing is rendered until a date AND a time exist. A past call opens with no slot chosen, and
			 * seeding the message with a made-up time (the current clock, or the old booking's) would put a
			 * wrong time in front of the user for as long as it took them to notice.
			 */
			renderMessage() {
				if (!this.selectedDate || !this.selectedTime) {
					return;
				}
				// The CONTACT-facing zone, not the admin's: this text is what the contact reads, and the
				// server writes the session name and the ICS summary in that same zone.
				//
				// Rendered from the selected INSTANT, so the wall clock in the message is the wall clock
				// the contact will experience — which is a different time of day from the one in the
				// picker whenever the zones differ, and correctly so.
				const selectedInstant = this.selectedMoment.clone().tz(this.contactFacingTimeZone);
				const tokens = renderScheduledCallTokens({
					selectedDate: selectedInstant.format("YYYY-MM-DD"),
					selectedTime: selectedInstant.format("h:mm A"),
					timeZone: this.contactFacingTimeZone,
					dateFormat: this.dateFormat
				});
				// Seed from the raw org template ONLY when there is nothing to lose: nothing has been
				// rendered into the box yet AND the user has not typed anything into it.
				//
				// The second half is not redundant. A call whose time has already passed opens with no
				// slot chosen, so nothing renders and `previousMessageTokens` stays null — but the box is
				// live and editable, so the user can write their whole message BEFORE picking a slot.
				// Keying the seed on `previousMessageTokens` alone then overwrote everything they had
				// typed the moment they picked a time, and their words never reached the contact.
				// // Bugbot review (Medium) "Time pick wipes typed reschedule message"
				//
				// When they have typed, their text is what gets rendered: pass 1 finds no previous values
				// to swap, and pass 2 still substitutes any `${...}` they wrote themselves.
				const hasRenderedBefore = this.previousMessageTokens !== null;
				const userHasTyped = (this.note ?? "") !== "";
				const { message } = applyScheduledCallMessageTokens(
					hasRenderedBefore || userHasTyped ? this.note : (this.messageTemplate ?? ""),
					tokens,
					{ teamMemberName: this.teamMemberName ?? "", organizationName: this.organizationName ?? "" },
					this.previousMessageTokens ?? {}
				);
				this.note = message;
				this.previousMessageTokens = tokens;
			},
			populateTimeSlots() {
				// 15-minute slots, 12:00 AM → 11:45 PM, matching ActionSchedule.populateTimes. Slots are
				// disabled only when the selected day IS today in the call's zone.
				const slots = [];
				const now = moment().tz(this.timeZone);
				const isToday = this.selectedDate === now.clone().startOf("day").format("YYYY-MM-DD");
				const floorMinutes = now.hours() * 60 + now.minutes();

				for (let minutesIntoDay = 0; minutesIntoDay < 1440; minutesIntoDay += SLOT_INTERVAL_MINUTES) {
					let hours = Math.floor(minutesIntoDay / 60);
					const minutes = String(minutesIntoDay % 60).padStart(2, "0");
					const meridiem = hours < 12 ? "AM" : "PM";
					hours = hours % 12 || 12;
					const label = `${hours}:${minutes} ${meridiem}`;
					slots.push({
						text: label,
						value: label,
						// `<=`, where ActionSchedule's create-time floor uses `<`. The slot landing on the
						// CURRENT minute is already in the past by however many seconds have elapsed, and the
						// server's gate is strictly-greater-than — so offering it would hand the user a
						// selection guaranteed to 400.
						disabled: isToday && minutesIntoDay <= floorMinutes
					});
				}
				this.timeSlots = slots;
			},
			close() {
				this.$emit("update:modelValue", false);
			},
			submit() {
				// Enabled-but-no-op: the agree button is never disabled on validity (CLAUDE.md → "Form
				// action buttons"), so the guard lives here. `saving` is the parent's prop — this dialog
				// never sets it, so the parent clearing it when its request settles is the only thing that
				// ever re-opens this guard.
				if (!this.isValid || this.saving) {
					return;
				}
				this.$emit("rescheduled", {
					startAt: this.selectedMoment.valueOf(),
					// The zone the message was RENDERED in, not the admin's working zone. The server
					// prefers the stored booking zone anyway, so for a call that has one these are the
					// same value; sending it keeps the empty-message server fill and the self-heal on the
					// appointment row agreeing with what this client just rendered.
					timeZone: this.contactFacingTimeZone,
					// Still the request's `note` field, which the endpoint already accepts and length-checks.
					// What it CARRIES changed (2026-09-17): it is now the whole contact-facing message, which
					// the server stores as `conversation.notificationTemplate`, rather than a preamble bolted
					// onto a generated sentence. The field was left named `note` deliberately — renaming it
					// would mean a route schema change, an OpenAPI change and a client/server deploy ordering
					// problem, for no behavioural difference.
					note: (this.note || "").trim()
				});
			}
		}
	};
</script>

<style scoped>
	/* LSP-16658: these hooks existed in the template from the first cut but carried NO rules, so
	   every one of them inherited a default that was too large and too light. Filling them in is
	   what closes the gap with the design; nothing here changes behaviour or selectors. */
	.reschedule-title {
		font-size: 1.5rem;
		font-weight: 700;
		line-height: 1.25;
		margin: 0;
	}

	.reschedule-timezone {
		font-size: 0.875rem;
		color: #6b6b76;
		margin-top: 2px;
	}

	.reschedule-label {
		font-size: 0.8125rem;
		font-weight: 600;
		color: #1f1f28;
		margin-bottom: 8px;
	}

	/* The calendar and the slot list read as one pair of framed panels in the design. Vuetify
	   renders neither with a border, so the frame lives here rather than on the components. */
	.reschedule-picker-panel {
		border: 1px solid #e0e0e6;
		border-radius: 12px;
		overflow: hidden;
	}

	/* The design frames the two pickers as an aligned pair. The calendar sets the height (Vuetify
	   reserves six week rows whatever the month), so the slot column stretches to match it and the
	   list scrolls inside rather than ending short. */
	/* Attribute selector, not `.v-col`. VCol emits the bare `v-col` class only when it generated no
	   `v-col-*` class OR no `cols` prop was given (vuetify/lib/components/VGrid/VCol.js:
	   `'v-col': !hasColClasses || !props.cols`). These columns are `cols="12" md="7|5"`, so both
	   conditions are false and the class is absent — a `.v-col` rule here matches nothing, which is
	   how this first shipped: the panels measured 346px vs 282px until the selector was changed. */
	.reschedule-pickers > [class*="v-col"] {
		display: flex;
		flex-direction: column;
	}

	.reschedule-pickers > [class*="v-col"] > .reschedule-picker-panel {
		flex: 1 1 auto;
		min-height: 0;
	}

	.reschedule-note-label {
		font-size: 0.875rem;
		margin-bottom: 8px;
	}

	.reschedule-note-hint {
		color: #6b6b76;
	}

	/* Separates the old-to-new summary from the message the contact receives. */
	.reschedule-note-block {
		border-top: 1px solid #e0e0e6;
		padding-top: 16px;
	}

	.reschedule-title-accent {
		background: linear-gradient(93deg, #019c9c 0%, #01bcbc 27.62%);
		background-clip: text;
		-webkit-background-clip: text;
		-webkit-text-fill-color: transparent;
	}

	.reschedule-current {
		background-color: #f5f5f5;
		border-radius: 12px;
		padding: 12px 16px;
		margin-bottom: 12px;
	}

	.reschedule-current-label {
		font-size: 0.6875rem;
		font-weight: 700;
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: #6b6b76;
		margin-bottom: 4px;
	}

	.reschedule-current-icon {
		color: #019c9c;
	}

	.reschedule-current-time {
		display: flex;
		align-items: center;
		gap: 8px;
	}

	.reschedule-time-list {
		/* A definite cap, NOT max-height:100%: the column's height comes from its own content, so a
		   percentage cap has no definite parent to resolve against and the list runs to full length
		   (measured 3,266px), dragging the dialog with it. */
		max-height: 280px;
		overflow-y: auto;
		padding-top: 0;
		padding-bottom: 0;
	}

	.reschedule-time-list :deep(.v-list-item) {
		min-height: 34px;
	}

	.reschedule-summary {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 8px;
		margin: 8px 0 16px;
		background-color: #e8f1fe;
		border-radius: 12px;
		padding: 12px 16px;
	}

	.reschedule-summary-from {
		text-decoration: line-through;
	}

	.reschedule-summary-to {
		font-weight: 600;
		color: rgb(var(--v-theme-primary));
	}

	.reschedule-error {
		color: #ff5252;
		width: 100%;
	}

	.reschedule-hint {
		color: #6b6b76;
		width: 100%;
	}
</style>
